/**
 * ProjectsListScreen — S2 Project Hub: list view + modal.
 * canonical: S2 lines 1303–1939
 *
 * Layout: .hub-grid (single-column project list).
 * Modal: ProjectModal, driven by URL search param ?pm=<projectId>.
 * Route /v4/projects/:id redirects to /v4/projects?pm=<id>.
 *
 * Live data: fetchProjects() — GET /api/projects (re-used from dashboard).
 * No Tailwind — all styles from tokens.css.
 */

import { useMemo, useState, useCallback } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueries } from '@tanstack/react-query'
import { fetchProjects, fetchProjectPayApps } from '../../api/projects'
import { getToken } from '../../api/client'
import type { Project }    from '../../api/types'
import { sumDraftPayAppAmount } from '../../lib/payAppDrafts.js'
import ProjectsToolbar, { type FilterChip } from './ProjectsToolbar'
import ProjectRow          from './ProjectRow'
import ProjectModal        from './ProjectModal'
import { formatCurrency } from '../../lib/formatters'
import { markHubPanelOpenStart } from '../../lib/hubPanelPerf'

// ── helpers ────────────────────────────────────────────────────────────────

function fmtContract(projects: Project[]): string {
  const total = projects.reduce((s, p) => s + parseFloat(p.original_contract ?? '0'), 0)
  return formatCurrency(total)
}

function matchesFilter(p: Project, filter: FilterChip): boolean {
  if (filter === 'all') return true
  const out = parseFloat(p.outstanding ?? '0')
  if (filter === 'active')    return p.status === 'active'
  if (filter === 'pay-due')   return out > 0 && p.status !== 'completed'
  if (filter === 'lien-risk') return out > 10_000 && p.status !== 'completed'
  if (filter === 'on-hold')   return p.status === 'draft'
  return true
}

function matchesSearch(p: Project, q: string): boolean {
  if (!q) return true
  const lq = q.toLowerCase()
  return (
    (p.name  ?? '').toLowerCase().includes(lq) ||
    (p.owner ?? '').toLowerCase().includes(lq) ||
    (p.contact ?? '').toLowerCase().includes(lq) ||
    (p.number ?? '').toLowerCase().includes(lq)
  )
}

// ── component ──────────────────────────────────────────────────────────────

export default function ProjectsListScreen() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  const [search,       setSearch]       = useState('')
  const [activeFilter, setActiveFilter] = useState<FilterChip>('all')

  // Live project list
  const { data: projects = [], isPending } = useQuery({
    queryKey:  ['projects'],
    queryFn:   fetchProjects,
    staleTime: 30_000,
  })

  // URL param → selected project for modal
  const pmId = searchParams.get('pm') ? parseInt(searchParams.get('pm')!, 10) : null
  const selectedProject = useMemo(
    () => (pmId ? projects.find(p => p.id === pmId) ?? null : null),
    [pmId, projects],
  )

  // Open modal: write ?pm=<id> to URL
  const openModal = useCallback((p: Project) => {
    markHubPanelOpenStart()
    setSearchParams({ pm: String(p.id) }, { replace: false })
  }, [setSearchParams])

  // Close modal: clear ?pm
  const closeModal = useCallback(() => {
    setSearchParams({}, { replace: false })
  }, [setSearchParams])

  // Filtered + searched list
  const visible = useMemo(
    () => projects.filter(p => matchesFilter(p, activeFilter) && matchesSearch(p, search)),
    [projects, activeFilter, search],
  )

  const activeCount = useMemo(
    () => projects.filter((p) => p.status === 'active').length,
    [projects],
  )

  const payAppQueries = useQueries({
    queries: projects.map(p => ({
      queryKey: ['project-payapps', p.id] as const,
      queryFn: () => fetchProjectPayApps(p.id),
      staleTime: 30_000,
      enabled: (p.pay_app_count ?? 0) > 0,
    })),
  })

  const draftTotalByProjectId = useMemo(() => {
    const map = new Map<number, number>()
    projects.forEach((p, i) => {
      const apps = payAppQueries[i]?.data
      if (apps) map.set(p.id, sumDraftPayAppAmount(apps))
    })
    return map
  }, [projects, payAppQueries])

  return (
    <>
      {/* topbar is rendered by V3Shell; we render only the hub-grid content */}

      {/* page topbar (inside .content) */}
      <div className="topbar">
        <div className="crumb">
          <b>Project Hub</b>
          <span style={{ color: 'var(--ink4)', fontWeight: 500, marginLeft: 6 }}>
            · {isPending ? '…' : `${activeCount} active`}
          </span>
        </div>
        <div className="topbar-r">
          <button
            type="button"
            className="btn b-ol btn-sm"
            title="Download all projects as CSV — every pay application row across your workspace"
            aria-label="Download all projects as CSV"
            data-testid="export-all-projects-csv"
            onClick={async () => {
              const token = getToken()
              const res = await fetch('/api/reports/export', {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
              })
              if (!res.ok) return
              const blob = await res.blob()
              const url = URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = 'all-projects-pay-apps.csv'
              a.click()
              URL.revokeObjectURL(url)
            }}
          >
            ↗ Export all
          </button>
          <button
            className="btn b-dk btn-sm"
            onClick={() => navigate('/projects/new')}
          >
            + New Project
          </button>
        </div>
      </div>

      <div className="hub-grid">
        <div>
          <ProjectsToolbar
            search={search}
            activeFilter={activeFilter}
            totalCount={projects.length}
            onSearch={setSearch}
            onFilter={setActiveFilter}
          />

          <div className="prj-list">
            <div className="prj-list-h">
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink4)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
                {isPending
                  ? 'Loading projects…'
                  : `${visible.length} project${visible.length !== 1 ? 's' : ''} · ${fmtContract(visible)} total contract value`
                }
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink4)' }}>
                Click any project for details
              </div>
            </div>

            {!isPending && visible.length === 0 && (
              <div className="pm-empty">
                <div className="pm-empty-ic">⊡</div>
                <div className="pm-empty-h">No projects found</div>
                <div className="pm-empty-st">
                  {search
                    ? `No projects match "${search}". Try a different search.`
                    : 'Create your first project to get started.'}
                </div>
                <button
                  className="btn b-dk pm-empty-cta"
                  onClick={() => navigate('/projects/new')}
                >
                  + New Project
                </button>
              </div>
            )}

            {visible.map(p => (
              <ProjectRow
                key={p.id}
                project={p}
                draftTotal={draftTotalByProjectId.get(p.id) ?? 0}
                onClick={openModal}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Project modal — driven by URL ?pm param */}
      <ProjectModal project={selectedProject} onClose={closeModal} />
    </>
  )
}
